import Head from 'next/head';
import { useRouter } from 'next/router';
import { ThemeProvider } from 'styled-components';
import parse from 'html-react-parser';

import { BlockEnvironmentProvider, BlockList, getBlocksData, getPost, initializeApollo } from '@zillow/cms-blocks';
import { RentalsB2BLayout } from '@zg-rentals/b2b-layout';
import { AppDownloadUpsellBanner } from '@zg-rentals/app-download-upsell-banner';
import { ThemeSkunkWorksCmsConstellation } from '@zillow/zrs-cms';

import SETTINGS from '../constants/settings';
import { bootstrap } from '../bootstrap/node';

export async function getStaticProps({ params }: { params: { slug: Array<string> } }) {
  const logger = bootstrap.logger.child({ name: 'pages/[slug]' });
  logger.warn(`getStaticProps: ${JSON.stringify(params)}`);

  // If no slug is provided, default to / to grab the front page
  const { slug = ['/'] } = params;

  logger.warn(`process.env.WP_GRAPHQL: ${process.env.WP_GRAPHQL}`);
  logger.warn(`process.env.ZG_ENV: ${process.env.ZG_ENV}`);
  logger.warn(`process.env.NEXT_PUBLIC_APEX_ENDPOINT: ${process.env.NEXT_PUBLIC_APEX_ENDPOINT}`);
  logger.warn(`slug.join('/'): ${slug.join('/')}`);

  initializeApollo();

  const { post: page } = await getPost({
    variables: {
      site: 'RENTALS',
      id: slug.join('/'),
      idType: 'URI',
      postType: 'PAGE',
    },
    fetchPolicy: 'no-cache',
  });

  logger.warn(`page.status: ${page?.status}, env: ${process.env.ZG_ENV}`);

  // Check for post status.
  if (page?.status !== 'publish') {
    return {
      notFound: true,
    };
  }

  const blocks = await getBlocksData(JSON.parse(page?.blocks || '[]'));

  return {
    props: {
      page: {
        ...page,
        blocks,
      },
      env: process.env.ZG_ENV || 'production',
      // for debugging ISR revalidation — note: multiple pods may show different values; convert to readable time for comparison
      generatedAt: Date.now(),
    },
    revalidate: SETTINGS.REVALIDATION_INTERVAL,
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export default function Page({
  page: { blocks, head },
  env,
  generatedAt,
}: {
  page: { blocks: Array<unknown>; head: string };
  env: string;
  generatedAt: number;
}) {
  const router = useRouter();

  // Account for production-docker
  const envProductionMap: { [key: string]: 'local' | 'dev' | 'test' | 'prod' | 'editor' } = {
    production: 'prod',
    'production-docker': 'prod',
  };

  // Account for block environment
  const blockEnvironmentMap: { [key: string]: string } = {
    production: 'prod',
    'production-docker': 'prod',
    development: 'dev',
  };

  const blockEnvironment = blockEnvironmentMap?.[env] || blockEnvironmentMap.development;

  const envValue = envProductionMap?.[env] || env;
  const bedrockStaticDomainMap: { [key: string]: string } = {
    production: 'https://www.zillowstatic.com',
    development: 'https://static.br1.zillow.net',
  };

  const bedrockStaticDomain = bedrockStaticDomainMap?.[envValue] || bedrockStaticDomainMap.development;

  return (
    <>
      {head && <Head>{parse(head)}</Head>}

      {bedrockStaticDomain && (
        <Head>
          <link rel="preconnect" href={bedrockStaticDomain} />
        </Head>
      )}

      <Head>
        <meta name="x-generated-at" content={String(generatedAt)} />
      </Head>

      <main>
        <ThemeProvider theme={ThemeSkunkWorksCmsConstellation}>
          <AppDownloadUpsellBanner />
        </ThemeProvider>

        <RentalsB2BLayout router={router} env={envValue} useVisualRefreshTheme>
          <BlockEnvironmentProvider environment={blockEnvironment}>
            <BlockList blocks={blocks} />
          </BlockEnvironmentProvider>
        </RentalsB2BLayout>
      </main>
    </>
  );
}
